<template>
  <CsvImport v-bind="{
    exampleData,
    entityName,
    rowName,
    parse,
    preview,
    upload
  }">
    <template #preview-item.shipping="{ value }">
      <p v-for="(addressLine, i) in formatAddress(value)" :key="`shippingaddr-line-${addressLine}-${i}`" class="ma-0">
        {{ addressLine }}
        <br>
      </p>
    </template>
    <template #preview-item.billing="{ value }">
      <p v-for="(addressLine, i) in formatAddress(value)" :key="`billingaddr-line-${addressLine}-${i}`" class="ma-0">
        {{ addressLine }}
        <br>
      </p>
    </template>
    <template #preview-item.tags="{ value }">
        <v-chip-group column>
          <v-chip
            small
            v-for="(tag, i) in formatTags(value).slice(0, 2)"
            :key="`tag-chip-${tag}-${i}`"
            :ripple="false"
          >
            {{ tag }}
          </v-chip>
          <Icon
          v-if="formatTags(value).length > 2"
          icon="mdi-dots-horizontal"
          iconColor="black"
          :tooltipText="formatTags(value).slice(2, formatTags(value).length).join(', ')"
          style="line-height: 2.25;"/>
        </v-chip-group>
    </template>
    <template #preview-item.trailerIds="{ value }">
      <v-chip-group>
        <v-chip
          small
          v-for="(trailerId, i) in normalizeTrailerIds(value).slice(0, 2)"
          :key="`trailerId-chip-${trailerId}-${i}`"
          :ripple="false"
          >{{ trailerId }}
        </v-chip>
          <Icon
          v-if="normalizeTrailerIds(value).length > 2"
          icon="mdi-dots-horizontal"
          iconColor="black"
          :tooltipText="normalizeTrailerIds(value).slice(2).join(', ')"
          style="line-height: 2.25;"/>
      </v-chip-group>
    </template>
    <template #preview-item.contacts="{ value }">
      <div class="d-flex">
        <ContactCard
          v-for="(contact, index) in convertContacts(value)"
          :key="`contact-${index}`"
          :contact="contact"
          :show-crud="false"
          class="mx-1 my-2"
          style="width: 300px;"
        />
      </div>
    </template>
  </CsvImport>
</template>

<script>
import { CsvColumn, CsvTypeTransformation } from '@/utils/csv/parse.js'
import { AddressType, TagArea, DeliveryFormat, DailyScaleDataFormat, VehicleType } from '@/utils/Enumerations.js'
import { accountBulkImportPreviewHeaders } from '@/headers/Account'
import { mapActions, mapGetters } from 'vuex'
import fieldRules from '@/utils/rules.js'
import { INITIAL_TIMEZONE_OFFSET, StringConstants } from '@/utils/constants.js'

const CONTACT_ROLES = [
  { key: 'dc', labelKey: 'defaultContact', requiresEmail: false, flags: { isDefault: true } },
  { key: 'ic', labelKey: 'insuranceContact', requiresEmail: true, flags: { isInsuranceContact: true } },
  { key: 'dtr', labelKey: 'dailyTicketReportRecipient', requiresEmail: true, flags: { dailyTicketReportPreference: DeliveryFormat.Pdf.value } },
  { key: 'sr', labelKey: 'settlementReportRecipient', requiresEmail: true, flags: { receivesSettlementReport: true } },
  { key: 'eudr', labelKey: 'eudrContact', requiresEmail: true, flags: { isEudrContact: true } },
  { key: 'dsd', labelKey: 'dailyScaleDataRecipient', requiresEmail: true, flags: { dailyScaleDataPreference: DailyScaleDataFormat.Csv.value } }
]

export default {
  name: 'AccountImports',

  components: {
    CsvImport: () => import('@/components/settings-components/imports/CsvImport.vue'),
    ContactCard: () => import('@/components/account/contacts/ContactCard.vue'),
    Icon: () => import('@/components/helper/Icon.vue')
  },

  data: () => ({
    accountTags: [],
    rules: fieldRules.rules
  }),

  computed: {
    ...mapGetters('user', ['companyInfo']),

    entityName () { return this.$t('account') },
    rowName () { return a => a.name },
    parse () {
      return {
        schema: async () => {
          await this.getTags()

          return {
            name: CsvColumn.from({
              name: 'Name',
              type: CsvTypeTransformation.from({
                validate: s => s === '' || s.length > StringConstants.LongStringLength ? this.$t('accountNameBlank') : true
              })
            }),
            code: CsvColumn.from({
              name: 'Code',
              type: CsvTypeTransformation.from({
                validate: s => s.length > StringConstants.ShortStringLength ? this.$t('accountCodeBlank') : true
              })
            }),
            exportCode: CsvColumn.from({
              name: 'ExportCode',
              type: CsvTypeTransformation.from({
                validate: s => s.length > StringConstants.MediumStringLength ? this.$t('accountExportCodeBlank') : true
              })
            }),
            shipping: {
              addressLine1: CsvColumn.from({ name: 'ShippingLine1' }),
              addressLine2: CsvColumn.from({ name: 'ShippingLine2' }),
              city: CsvColumn.from({ name: 'ShippingCity' }),
              postalCode: CsvColumn.from({ name: 'ShippingPostalCode' }),
              state: CsvColumn.from({ name: 'ShippingState' })
            },
            billing: {
              addressLine1: CsvColumn.from({ name: 'BillingLine1' }),
              addressLine2: CsvColumn.from({ name: 'BillingLine2' }),
              city: CsvColumn.from({ name: 'BillingCity' }),
              postalCode: CsvColumn.from({ name: 'BillingPostalCode' }),
              state: CsvColumn.from({ name: 'BillingState' })
            },
            tags: {
              tag1: CsvColumn.from({ name: 'Tag1', required: false }),
              tag2: CsvColumn.from({ name: 'Tag2', required: false }),
              tag3: CsvColumn.from({ name: 'Tag3', required: false }),
              tag4: CsvColumn.from({ name: 'Tag4', required: false }),
              tag5: CsvColumn.from({ name: 'Tag5', required: false })
            },
            contacts: {
              dcFirstName: CsvColumn.from({ name: 'DC_FirstName', required: false }),
              dcLastName: CsvColumn.from({ name: 'DC_LastName', required: false }),
              dcPhoneNumber: CsvColumn.from({ name: 'DC_PhoneNumber', required: false }),
              dcEmail: CsvColumn.from({ name: 'DC_Email', required: false }),
              icFirstName: CsvColumn.from({ name: 'IC_FirstName', required: false }),
              icLastName: CsvColumn.from({ name: 'IC_LastName', required: false }),
              icPhoneNumber: CsvColumn.from({ name: 'IC_PhoneNumber', required: false }),
              icEmail: CsvColumn.from({ name: 'IC_Email', required: false }),
              dtrFirstName: CsvColumn.from({ name: 'DTR_FirstName', required: false }),
              dtrLastName: CsvColumn.from({ name: 'DTR_LastName', required: false }),
              dtrPhoneNumber: CsvColumn.from({ name: 'DTR_PhoneNumber', required: false }),
              dtrEmail: CsvColumn.from({ name: 'DTR_Email', required: false }),
              srFirstName: CsvColumn.from({ name: 'SR_FirstName', required: false }),
              srLastName: CsvColumn.from({ name: 'SR_LastName', required: false }),
              srPhoneNumber: CsvColumn.from({ name: 'SR_PhoneNumber', required: false }),
              srEmail: CsvColumn.from({ name: 'SR_Email', required: false }),
              eudrFirstName: CsvColumn.from({ name: 'EUDR_FirstName', required: false }),
              eudrLastName: CsvColumn.from({ name: 'EUDR_LastName', required: false }),
              eudrPhoneNumber: CsvColumn.from({ name: 'EUDR_PhoneNumber', required: false }),
              eudrEmail: CsvColumn.from({ name: 'EUDR_Email', required: false }),
              dsdFirstName: CsvColumn.from({ name: 'DSD_FirstName', required: false }),
              dsdLastName: CsvColumn.from({ name: 'DSD_LastName', required: false }),
              dsdPhoneNumber: CsvColumn.from({ name: 'DSD_PhoneNumber', required: false }),
              dsdEmail: CsvColumn.from({ name: 'DSD_Email', required: false })
            },
            trailerIds: {
              trailerId1: CsvColumn.from({ name: 'TrailerId1', required: false }),
              trailerId2: CsvColumn.from({ name: 'TrailerId2', required: false }),
              trailerId3: CsvColumn.from({ name: 'TrailerId3', required: false }),
              trailerId4: CsvColumn.from({ name: 'TrailerId4', required: false }),
              trailerId5: CsvColumn.from({ name: 'TrailerId5', required: false })
            }
          }
        },

        validate: account => {
          const errors = []
          for (const address of [account.shipping, account.billing].filter(a => a.addressLine1)) {
            if (!address.city) errors.push(this.$t('addressMissingCity'))
            if (!address.state) errors.push(this.$t('addressMissingState'))
            if (!address.postalCode) errors.push(this.$t('addressMissingPostalCode'))
          }

          const companyTzOffset = this.companyInfo.timeZoneOffset
          if (companyTzOffset === INITIAL_TIMEZONE_OFFSET) {
            errors.push(this.$t('noTimeZone'))
          }

          const { tag1, tag2, tag3, tag4, tag5 } = account.tags
          const tags = [tag1, tag2, tag3, tag4, tag5]

          for (const tag of tags) {
            if (!this.accountTags.map(t => t.value).includes(tag) && tag) errors.push(this.$t('invalidTag', { tagName: tag }))
            if (tags.filter(t => t === tag && t).length > 1 && !errors.includes(this.$t('multipleTags', { tagName: tag }))) {
              errors.push(this.$t('multipleTags', { tagName: tag }))
            }
          }

          if (tags.findLastIndex(e => e !== '') > tags.findIndex(e => e === '') && tags.includes('')) {
            errors.push(this.$t('invalidTagOrdering'))
          }

          errors.push(...this.validateContacts(account.contacts))

          const trailerIds = this.normalizeTrailerIds(account.trailerIds)
          if (trailerIds.some(t => !this.isValidTrailerId(t))) {
            errors.push(this.$t('invalidAccountTrailerId'))
          }

          return errors
        }
      }
    },

    preview: () => ({
      headers: accountBulkImportPreviewHeaders()
    }),

    upload () {
      return {
        map: ({ name, code, exportCode, shipping, billing, tags, contacts, trailerIds }) => ({
          name,
          code,
          exportCode,
          timeZoneOffset: this.companyInfo.timeZoneOffset,
          addresses: [
            shipping.addressLine1 ? { address: { ...shipping, type: AddressType.ShipTo.value } } : undefined,
            billing.addressLine1 ? { address: { ...billing, type: AddressType.BillTo.value } } : undefined
          ].filter(a => a),
          tagIds: this.getTagIds(tags),
          contacts: this.convertContacts(contacts),
          accountDriverDetail: {
            trailerIdentifiers: this.normalizeTrailerIds(trailerIds).map(t => ({ value: t, vehicleType: VehicleType.Trailer.value }))
          }
        }),

        action: this.createAccount
      }
    },

    exampleData () {
      const header = [
        'Name', 'Code', 'ExportCode',
        'BillingLine1', 'BillingLine2', 'BillingCity', 'BillingPostalCode', 'BillingState',
        'ShippingLine1', 'ShippingLine2', 'ShippingCity', 'ShippingPostalCode', 'ShippingState',
        'Tag1', 'Tag2', 'Tag3', 'Tag4', 'Tag5',
        'DC_FirstName', 'DC_LastName', 'DC_PhoneNumber', 'DC_Email',
        'IC_FirstName', 'IC_LastName', 'IC_PhoneNumber', 'IC_Email',
        'DTR_FirstName', 'DTR_LastName', 'DTR_PhoneNumber', 'DTR_Email',
        'SR_FirstName', 'SR_LastName', 'SR_PhoneNumber', 'SR_Email',
        'EUDR_FirstName', 'EUDR_LastName', 'EUDR_PhoneNumber', 'EUDR_Email',
        'DSD_FirstName', 'DSD_LastName', 'DSD_PhoneNumber', 'DSD_Email',
        'TrailerId1', 'TrailerId2', 'TrailerId3', 'TrailerId4', 'TrailerId5'
      ]

      const rows = [
        [
          'Congaree Forestry Services', 'AC3082', 'EX9132',
          '500 W 5th St', 'Suite 500', 'Winston-Salem', '27101', 'NC',
          '500 W 5th St', 'Suite 500', 'Winston-Salem', '27101', 'NC',
          'Vendor', 'Supplier', 'Truck', 'Mill', 'Logger',
          'Default', 'Contact', '1234567890', 'default@example.com',
          'Insurance', 'Contact', '2345678901', 'insurancecontact@example.com',
          'Daily', 'Ticket Report', '3456789012', 'dailyticketreport@example.com',
          'Settlement', 'Report', '4567890123', 'settlementreport@example.com',
          'EUDR', 'Contact', '5678901234', 'eudrcontact@example.com',
          'Daily', 'Scale Data', '6789012345', 'dailyscaledata@example.com',
          '1', '2', '3', '', ''
        ],
        [
          'Rax Trucking', 'AC5268', 'EX1923',
          '500 W 5th St', 'Suite 502', 'Winston-Salem', '27101', 'NC',
          '500 W 5th St', 'Suite 502', 'Winston-Salem', '27101', 'NC',
          'Customer', 'Vendor', '', '', '',
          'Yard', 'Foreman', '', '',
          '', '', '', '',
          'Example', 'Contact', '1235879609', 'examplecontact@example.com',
          'Example', 'Contact', '', 'examplecontact@example.com',
          '', '', '', '',
          '', '', '', '',
          '', '', '', '', ''
        ]
      ]

      return 'data:text/csv;charset=utf-8,' + [header, ...rows].map(r => r.join(',') + '\n').join('')
    }
  },

  methods: {
    ...mapActions('account', ['createAccount']),
    ...mapActions('tags', ['fetchTags']),

    formatAddress (addr) {
      if (!addr.addressLine1 || !addr.city || !addr.state || !addr.postalCode) {
        return [this.$t('none')]
      } else {
        return addr.addressLine2
          ? [addr.addressLine1, addr.addressLine2, addr.city + ', ' + addr.state + ' ' + addr.postalCode]
          : [addr.addressLine1, addr.city + ', ' + addr.state + ' ' + addr.postalCode]
      }
    },

    formatTags (tags) {
      return Object.values(tags).filter(t => t && t.trim() !== '')
    },

    normalizeTrailerIds (trailerIds) {
      const values = Array.isArray(trailerIds)
        ? trailerIds
        : trailerIds && typeof trailerIds === 'object'
          ? Object.values(trailerIds)
          : []

      return values
        .map(v => (v == null ? '' : String(v).trim()))
        .filter(v => v !== '')
    },

    isValidTrailerId (value) {
      return /^[1-9]\d*$/.test(value) && value.length <= StringConstants.ShortStringLength
    },

    async getTags () {
      const tags = await this.fetchTags(TagArea.Accounts.value)
      this.accountTags = tags
    },

    getTagIds (tags) {
      const filteredTags = Object.values(tags).filter(t => t)
      return this.accountTags.filter(t => filteredTags.includes(t.value)).map(t => t.tagId)
    },

    providedContactRoles (contacts) {
      return CONTACT_ROLES
        .map(role => {
          const field = name => (contacts[`${role.key}${name}`] ?? '').trim()
          return {
            role,
            firstName: field('FirstName'),
            lastName: field('LastName'),
            phoneNumber: field('PhoneNumber'),
            email: field('Email')
          }
        })
        .filter(({ firstName, lastName, phoneNumber, email }) => firstName || lastName || phoneNumber || email)
    },

    convertContacts (contacts) {
      const converted = []

      for (const { role, firstName, lastName, phoneNumber, email } of this.providedContactRoles(contacts)) {
        const existing = email && converted.find(c => c.person.email?.toLowerCase() === email.toLowerCase())

        if (existing) {
          Object.assign(existing, role.flags)
        } else {
          converted.push({
            person: {
              firstName,
              lastName,
              phoneNumber: phoneNumber || null,
              email: email || null
            },
            ...role.flags
          })
        }
      }

      if (converted.length === 1) {
        converted[0].isDefault = true
      }

      return converted
    },

    validateContacts (contacts) {
      const errors = []

      for (const { role, firstName, lastName, phoneNumber, email } of this.providedContactRoles(contacts)) {
        const contactType = this.$t(role.labelKey)

        if (!firstName || !lastName) {
          errors.push(this.$t('contactIncomplete', { contactType }))
          continue
        }

        if (role.requiresEmail && !email) {
          errors.push(this.$t('contactEmailRequired', { contactType }))
        }

        if (firstName.length > StringConstants.LongStringLength) {
          errors.push(this.$t('contactNameTooLong', { contactType, name: this.$t('firstName') }))
        }

        if (lastName.length > StringConstants.LongStringLength) {
          errors.push(this.$t('contactNameTooLong', { contactType, name: this.$t('lastName') }))
        }

        if (phoneNumber && phoneNumber.replace(/\D/g, '').length !== 10) {
          errors.push(this.$t('contactPhoneInvalid', { contactType }))
        }

        if (email && this.rules.email(email) !== true) {
          errors.push(this.$t('contactEmailInvalid', { contactType }))
        }

        if (role.key === 'ic' && this.companyInfo.insuranceContact == null) {
          errors.push(this.$t('setInsuranceComplianceUser'))
        }
      }

      return errors
    }
  }
}
</script>
